import { Select, Pagination, Tooltip } from 'antd';
import { ClipLoader } from 'react-spinners';
import {
  ArrowCounterClockwise,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  Check,
  ClockCountdown,
  DotsThreeVertical,
  Invoice,
  Prohibit,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  TransactionWithPayments,
  BudgetCategory,
} from '../../../../../../types/apiTypes';
import { Truck } from '../../../../../../types/dbTypes';
import { formatDate } from '../../../../../../utils/DateUtils';
import {
  getAttributionTrucks,
  getInitialSelectedTruckIds,
} from '../../../../../../utils/attributionUtils';
import { unicodeToEmoji } from '../../../../../../shared/emojiUnicode';
import { transactionCategoriesBaseMap } from '../../../../../../types/apiTypes';
import { dontCategorizeCategorySet } from '../../../../../../types/constants';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { formatCurrency } from '../../../../../../utils/CurrencyUtils';
import React, { useEffect, useState } from 'react';
import { delay } from '../../../../../../utils/Delay';
import { DemoDataWrapper } from '../../home/components/DemoDataBadge';
import toast from 'react-hot-toast';
import { createPortal } from 'react-dom';

import { useGetInvoiceMutation } from '../hooks/useTransactions';
import TruckMultiSelect from '../../../../../shared/TruckMultiSelect';

const HEADERS = [
  'Date',
  'Merchant',
  'Category',
  'Truck',
  'Card Last 4',
  'Status',
  'Amount',
];

const PAYMENTS_HEADERS = ['Date', 'Description', 'Status', 'Amount'];

const COL = {
  date: 'lg:w-[120px]',
  merchant: 'lg:w-[28%] lg:min-w-[280px] min-w-[200px] w-[200px]',
  category: 'lg:w-[26%] lg:min-w-[240px]',
  truck: 'lg:w-[30%] lg:min-w-[250px] min-w-[120px] w-[120px]',
  last4: 'w-[90px] lg:w-[110px] lg:min-w-[110px]',
  status: 'lg:w-[140px] lg:min-w-[140px]',
  amount: 'lg:w-[120px] lg:min-w-[120px]',
  action: 'lg:w-[120px] lg:min-w-[120px]',
};

// Shared layout for the full-height table placeholder states (error, empty)
// so the two branches can't drift apart visually.
function TableStatusPanel({
  icon,
  message,
  action,
}: {
  icon: React.ReactNode;
  message: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex h-[calc(100vh-200px)] w-full flex-col items-center justify-center space-y-2">
      <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
        {icon}
      </div>
      <p className="text-[14px] leading-[20px] text-graysecondary">{message}</p>
      {action}
    </div>
  );
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded-[4px] border-[1px] border-bordergray bg-white px-4 py-2 text-[14px] text-primary hover:bg-gray-100"
    >
      Retry
    </button>
  );
}

interface TransactionTableProps {
  transactions: TransactionWithPayments[];
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  isCountError?: boolean;
  onRetryCount?: () => void;
  transactionsCount: number;
  isLoadingCount: boolean;
  offset: number;
  limit: number;
  perPage: number;
  activePopup: string | null;
  popupRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  togglePopup: (transaction_id: string | undefined) => void;
  handleAttachInvoice: (transaction: TransactionWithPayments) => void;
  handlePerPageChange: (value: number) => void;
  handlePageChange: (page: number, pageSize: number) => void;
  handleCategoryChange: (value: string, transaction_id: string) => void;
  handleTruckChange: (value: string, transaction_id: string) => void;
  handleOpenSplitModal: (
    transaction: TransactionWithPayments,
    selectedTrucks: Truck[]
  ) => void;
  handleClearTruckAttributions: (transaction_id: string) => Promise<void>;
  budgetCategories: BudgetCategory[];
  trucks: Truck[];
  isPreActive: boolean;
}

function TransactionTable({
  transactions,
  isLoading,
  isError,
  onRetry,
  isCountError,
  onRetryCount,
  transactionsCount,
  isLoadingCount,
  offset,
  limit,
  perPage,
  activePopup,
  popupRefs,
  togglePopup,
  handleAttachInvoice,
  handlePerPageChange,
  handlePageChange,
  handleCategoryChange,
  handleTruckChange,
  handleOpenSplitModal,
  handleClearTruckAttributions,
  budgetCategories,
  trucks,
  isPreActive,
}: TransactionTableProps) {
  type PendingMap = Record<string, { category?: boolean; truck?: boolean }>;
  const [pending, setPending] = useState<PendingMap>({});
  const [localCategory, setLocalCategory] = useState<Record<string, string>>(
    {}
  );
  const [localTruck, setLocalTruck] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Track selected truck IDs per transaction for multi-select
  const [selectedTruckIds, setSelectedTruckIds] = useState<
    Record<string, string[]>
  >({});
  const [buttonPositions, setButtonPositions] = useState<{
    [key: string]: { top: number; left: number; width: number; height: number };
  }>({});
  const [loadingInvoiceId, setLoadingInvoiceId] = useState<string | null>(null);

  const toggle = (transactionId: string) => {
    console.log('transactionId', transactionId);

    setExpanded((s) => ({ ...s, [transactionId]: !s[transactionId] }));
  };

  const { screenView } = useScreenView();
  const { mutateAsync: getInvoice } = useGetInvoiceMutation();
  const getStatusIcon = (
    processed: boolean,
    processing_completed_timestamp: Date | null
  ) => {
    if (processed) {
      return <Check size={20} className="text-green-500" />;
    } else if (!processed && processing_completed_timestamp === null) {
      return <ClockCountdown size={20} className="text-blue-500" />;
    } else {
      return <Prohibit size={20} className="text-red-500" />;
    }
  };

  useEffect(() => {
    setLocalCategory({});
    setLocalTruck({});
    // Initialize selected truck IDs from existing transaction data
    // Prefer truckAttributions if they exist, otherwise fall back to truck_id
    const initialSelectedTrucks: Record<string, string[]> = {};
    transactions.forEach((t) => {
      initialSelectedTrucks[t.transaction_id] = getInitialSelectedTruckIds(t);
    });
    setSelectedTruckIds(initialSelectedTrucks);
  }, [transactions]);

  const getStatusText = (
    processed: boolean,
    processing_completed_timestamp: Date | null
  ) => {
    if (processed) {
      return 'Processed';
    } else if (!processed && processing_completed_timestamp === null) {
      return 'Processing';
    } else {
      return 'Failed';
    }
  };

  const getStatusBreakdownIcon = (status: string) => {
    if (status === 'Processed') {
      return <Check size={20} className="text-green-500" />;
    } else if (status === 'Initiated') {
      return <ClockCountdown size={20} className="text-blue-500" />;
    } else {
      return <Prohibit size={20} className="text-primary" />;
    }
  };

  const getStatusBreakdownText = (status: string) => {
    if (status === 'Processed') {
      return 'Processed';
    } else if (status === 'Initiated') {
      return 'Initiated';
    } else {
      return 'Failed';
    }
  };

  const toDisplayAmount = (raw?: number | null) => {
    if (raw == null) return 0;
    return -(raw / 100);
  };

  const getDisplayAmountClass = (displayAmount: number) =>
    displayAmount > 0 ? 'text-green-500' : 'text-primary';

  const setFieldPending = (
    id: string,
    field: 'category' | 'truck',
    value: boolean
  ) =>
    setPending((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));

  // Helpers to read/write the current value (prefer local mirrors)
  const getCategoryValue = (t: TransactionWithPayments) =>
    localCategory[t.transaction_id] ?? t.category ?? '';

  const getTruckValue = (t: TransactionWithPayments) =>
    localTruck[t.transaction_id] ?? t.truck_id ?? '';

  // Optimistic handlers. Failures are toasted, not rethrown: antd fires
  // these from un-awaited event callbacks, so a rethrow would surface as an
  // unhandled promise rejection instead of user feedback.
  const onCategoryChange = async (value: string, transaction_id: string) => {
    if (isPreActive) {
      toast.error('Complete onboarding to modify transactions');
      return;
    }
    const prev = localCategory[transaction_id];
    setLocalCategory((m) => ({ ...m, [transaction_id]: value }));
    setFieldPending(transaction_id, 'category', true);
    try {
      await handleCategoryChange(value, transaction_id);
    } catch {
      // Revert on failure. When there was no local override, drop the key
      // entirely so getCategoryValue falls back to the server category —
      // writing '' here would render the row as if the category were cleared.
      setLocalCategory((m) => {
        const next = { ...m };
        if (prev === undefined) {
          delete next[transaction_id];
        } else {
          next[transaction_id] = prev;
        }
        return next;
      });
      toast.error('Failed to update category. Please try again.');
    } finally {
      await delay(1000);
      setFieldPending(transaction_id, 'category', false);
    }
  };

  // Handler for updating selected trucks in multi-select
  const handleTruckSelectionChange = (
    transactionId: string,
    truckIds: string[]
  ) => {
    setSelectedTruckIds((prev) => ({ ...prev, [transactionId]: truckIds }));
  };

  // Handler for saving single truck selection
  const handleSingleTruckSave = async (
    transactionId: string,
    truckId: string
  ) => {
    if (isPreActive) {
      toast.error('Complete onboarding to modify transactions');
      return;
    }
    setFieldPending(transactionId, 'truck', true);
    try {
      await handleTruckChange(truckId, transactionId);
      // Update local state
      setLocalTruck((m) => ({ ...m, [transactionId]: truckId }));
      setSelectedTruckIds((prev) => ({ ...prev, [transactionId]: [truckId] }));
    } catch {
      toast.error('Failed to update truck. Please try again.');
    } finally {
      await delay(1000);
      setFieldPending(transactionId, 'truck', false);
    }
  };

  // Handler for assigning a transaction to the business (no truck). Clears any
  // truck attribution — the backend nulls the truck_id. Business is a valid
  // resting state, so an empty selection then renders as "Business".
  const handleAssignBusiness = async (transactionId: string) => {
    if (isPreActive) {
      toast.error('Complete onboarding to modify transactions');
      return;
    }
    setFieldPending(transactionId, 'truck', true);
    try {
      await handleClearTruckAttributions(transactionId);
      setLocalTruck((m) => ({ ...m, [transactionId]: '' }));
      setSelectedTruckIds((prev) => ({ ...prev, [transactionId]: [] }));
    } catch {
      toast.error('Failed to update truck. Please try again.');
    } finally {
      await delay(1000);
      setFieldPending(transactionId, 'truck', false);
    }
  };

  // Handler for opening split modal
  const handleOpenSplitModalForTransaction = (
    transaction: TransactionWithPayments
  ) => {
    const selectedIds = selectedTruckIds[transaction.transaction_id] || [];
    const selectedTruckObjects = trucks.filter((t) =>
      selectedIds.includes(t.truck_id)
    );
    handleOpenSplitModal(transaction, selectedTruckObjects);
  };

  const renderTable = () => {
    const tableWidth =
      screenView === 'mobile'
        ? 'min-w-[1000px]'
        : screenView === 'tablet'
          ? 'min-w-[930px]'
          : 'w-full';

    return (
      <div className={`${tableWidth}`}>
        {!isLoading ? (
          <table className="w-full table-fixed">
            <thead className="sticky top-0 z-20 h-[50px] max-h-[50px] bg-primarybg shadow-sm">
              <tr className="h-[50px] max-h-[50px]">
                <th className="h-[50px] max-h-[50px] w-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary"></th>

                <th
                  className={`px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary ${COL.date}`}
                >
                  Date
                </th>
                <th
                  className={`px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary ${COL.merchant}`}
                >
                  Merchant
                </th>
                <th
                  className={`px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary ${COL.category}`}
                >
                  Category
                </th>
                <th
                  className={`px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary ${COL.truck}`}
                >
                  Truck
                </th>
                <th
                  className={`px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary ${COL.last4}`}
                >
                  Card Last 4
                </th>
                <th
                  className={`px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary ${COL.status}`}
                >
                  Status
                </th>
                <th
                  className={`px-4 py-2 text-right text-[14px] font-[600] leading-[18px] text-primary ${COL.amount}`}
                >
                  Amount
                </th>
                <th
                  className={`px-4 py-2 text-right text-[14px] font-[600] leading-[18px] text-primary ${COL.action}`}
                ></th>
              </tr>
            </thead>
            <tbody className="table-fixed">
              {transactions.map((transaction, index) => {
                const catVal = getCategoryValue(transaction);
                const truckVal = getTruckValue(transaction);
                const isCatPending =
                  !!pending[transaction.transaction_id]?.category;
                const isTruckPending =
                  !!pending[transaction.transaction_id]?.truck;

                const isTransactionReversed =
                  transaction.reversal_status != null;

                const canEditCategory =
                  !isTransactionReversed &&
                  (transaction?.amount ?? 0) < 0 &&
                  !dontCategorizeCategorySet.has(
                    catVal || transaction.category || ''
                  );

                const categoryDisplay = Object.values(
                  transactionCategoriesBaseMap
                ).find(
                  (item) =>
                    item?.categoryName === (catVal || transaction?.category)
                );

                const isPayments = !!transaction?.payments?.length;
                const isOpen = !!expanded[transaction.transaction_id];

                return (
                  <React.Fragment key={transaction?.transaction_id}>
                    <tr
                      className={`h-[50px] max-h-[50px] overflow-hidden border-b border-gray-200 ${isTransactionReversed ? 'opacity-60' : ''}`}
                      style={
                        !isPayments
                          ? { cursor: 'default' }
                          : { cursor: 'pointer' }
                      }
                    >
                      <td
                        className="cursor-pointer px-[11px] py-2 align-middle"
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                        onClick={
                          isPayments
                            ? () => toggle(transaction.transaction_id)
                            : undefined
                        }
                      >
                        {isPayments ? (
                          isOpen ? (
                            <CaretUp size={18} className="text-primary" />
                          ) : (
                            <CaretDown size={18} className="text-primary" />
                          )
                        ) : null}
                      </td>

                      <td
                        className={`h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary ${COL.date}`}
                        onClick={
                          isPayments
                            ? () => toggle(transaction.transaction_id)
                            : undefined
                        }
                      >
                        {formatDate(new Date(transaction?.created_at ?? ''))}
                      </td>
                      <td
                        className={`h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary ${COL.merchant}`}
                        onClick={
                          isPayments
                            ? () => toggle(transaction.transaction_id)
                            : undefined
                        }
                      >
                        <Tooltip
                          title={
                            transaction.merchant_name ?? transaction.description
                          }
                          placement="top"
                          mouseEnterDelay={0.3}
                        >
                          <span className="inline-block w-full truncate">
                            {transaction.merchant_name ??
                              transaction.description}
                          </span>
                        </Tooltip>
                      </td>

                      {/* CATEGORY */}

                      <td
                        className={`h-[50px] max-h-[50px] px-4 py-2 text-[14px] text-primary ${COL.category}`}
                      >
                        {canEditCategory ? (
                          <div className="flex w-full items-center">
                            <div className="w-full">
                              {(() => {
                                const currentVal =
                                  catVal || transaction.category || '';
                                const selectedOption =
                                  (currentVal === 'unknown'
                                    ? {
                                        categoryName: 'unknown',
                                        emoji_unicode: 'U+2753',
                                        displayName: 'Unknown',
                                      }
                                    : budgetCategories.find(
                                        (c) => c.categoryName === currentVal
                                      )) || null;

                                const selectedLabel = selectedOption
                                  ? `${unicodeToEmoji(selectedOption.emoji_unicode)} ${selectedOption.displayName}`
                                  : '';

                                return (
                                  <Tooltip
                                    title={selectedLabel}
                                    placement="top"
                                    mouseEnterDelay={0.3}
                                  >
                                    <Select
                                      value={currentVal || undefined}
                                      onChange={(value) =>
                                        onCategoryChange(
                                          value,
                                          transaction.transaction_id
                                        )
                                      }
                                      placeholder="Select Category"
                                      className={`w-full ${!catVal ? 'ant-select-status-warning' : ''}`}
                                      disabled={isCatPending}
                                      suffixIcon={
                                        isCatPending ? (
                                          <ClipLoader size={14} />
                                        ) : undefined
                                      }
                                      optionLabelProp="label"
                                    >
                                      {[
                                        ...(currentVal === 'unknown'
                                          ? [
                                              {
                                                categoryName: 'unknown',
                                                emoji_unicode: 'U+2753',
                                                displayName: 'Unknown',
                                              },
                                            ]
                                          : []),
                                        ...budgetCategories,
                                      ].map((category) => (
                                        <Select.Option
                                          key={category.categoryName}
                                          value={category.categoryName}
                                          label={`${unicodeToEmoji(category.emoji_unicode)} ${category.displayName}`}
                                        >
                                          {/* Truncate visually in the dropdown but show full on hover */}
                                          <Tooltip
                                            title={`${unicodeToEmoji(category.emoji_unicode)} ${category.displayName}`}
                                            placement="right"
                                            mouseEnterDelay={0.2}
                                          >
                                            <div className="truncate">
                                              {unicodeToEmoji(
                                                category.emoji_unicode
                                              )}{' '}
                                              {category.displayName}
                                            </div>
                                          </Tooltip>
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  </Tooltip>
                                );
                              })()}
                            </div>

                            {!catVal && !isCatPending && (
                              <WarningIcon
                                size={28}
                                className="ml-2 text-orange-500"
                              />
                            )}
                          </div>
                        ) : (
                          <Tooltip
                            title={`${unicodeToEmoji(categoryDisplay?.emoji_unicode)} ${categoryDisplay?.displayName}`}
                            placement="top"
                            mouseEnterDelay={0.3}
                          >
                            <span>
                              {unicodeToEmoji(categoryDisplay?.emoji_unicode)}{' '}
                              {categoryDisplay?.displayName}
                            </span>
                          </Tooltip>
                        )}
                      </td>

                      {/* TRUCK */}
                      <td
                        className={`h-[50px] max-h-[50px] px-4 py-2 text-[14px] text-primary ${COL.truck}`}
                      >
                        <div className="flex w-full items-center">
                          <TruckMultiSelect
                            trucks={trucks}
                            placeholder="Business"
                            selectedTruckIds={
                              selectedTruckIds[transaction.transaction_id] ||
                              (truckVal ? [truckVal] : [])
                            }
                            onSelectionChange={(truckIds) =>
                              handleTruckSelectionChange(
                                transaction.transaction_id,
                                truckIds
                              )
                            }
                            onSingleSave={(truckId) =>
                              handleSingleTruckSave(
                                transaction.transaction_id,
                                truckId
                              )
                            }
                            onOpenSplitModal={() =>
                              handleOpenSplitModalForTransaction(transaction)
                            }
                            onAssignBusiness={() =>
                              handleAssignBusiness(transaction.transaction_id)
                            }
                            disabled={isTruckPending || isTransactionReversed}
                            isLoading={isTruckPending}
                            additionalTrucks={getAttributionTrucks(transaction)}
                            existingAttributions={transaction.truckAttributions}
                          />
                        </div>
                      </td>

                      {/* CARD LAST 4 */}
                      <td
                        className={`h-[50px] max-h-[50px] overflow-hidden whitespace-nowrap px-4 py-2 text-[14px] text-primary ${COL.last4}`}
                        onClick={
                          isPayments
                            ? () => toggle(transaction.transaction_id)
                            : undefined
                        }
                      >
                        {transaction?.debit_card?.last_four_digits || '--'}
                      </td>

                      {/* STATUS */}
                      <td
                        className={`flex h-[50px] max-h-[50px] flex-row items-center gap-x-2 overflow-hidden whitespace-nowrap px-4 py-2 text-[14px] text-primary ${COL.status}`}
                        onClick={
                          isPayments
                            ? () => toggle(transaction.transaction_id)
                            : undefined
                        }
                      >
                        {isTransactionReversed ? (
                          <Tooltip
                            title="Funds returned to your account"
                            placement="top"
                            mouseEnterDelay={0.3}
                          >
                            <span className="flex items-center gap-x-1 text-gray-500">
                              <ArrowCounterClockwise size={16} weight="bold" />
                              Reversed
                            </span>
                          </Tooltip>
                        ) : (
                          <>
                            {getStatusIcon(
                              transaction?.processed ?? false,
                              transaction?.processing_completed_timestamp ??
                                null
                            )}{' '}
                            {getStatusText(
                              transaction?.processed ?? false,
                              transaction?.processing_completed_timestamp ??
                                null
                            )}
                          </>
                        )}
                      </td>

                      {/* AMOUNT */}
                      <td
                        className={`h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-right text-[14px] ${COL.amount} ${
                          isTransactionReversed
                            ? 'text-gray-400'
                            : transaction?.amount && transaction?.amount > 0
                              ? 'text-green-500'
                              : 'text-primary'
                        }`}
                        onClick={
                          isPayments
                            ? () => toggle(transaction.transaction_id)
                            : undefined
                        }
                      >
                        <span
                          className={
                            isTransactionReversed ? 'line-through' : ''
                          }
                        >
                          {formatCurrency(transaction?.amount ?? 0)}
                        </span>
                      </td>
                      {!isPreActive &&
                        transaction?.amount &&
                        transaction?.amount > 0 && (
                          <td
                            className={`flex h-[50px] max-h-[50px] cursor-pointer items-center justify-center overflow-hidden whitespace-nowrap py-2 pl-4 pr-6 text-[14px] text-primary ${COL.action}`}
                          >
                            <div className="ml-4 flex items-center justify-center gap-2">
                              {transaction.deposit_invoice?.upload_confirmed ? (
                                <Tooltip
                                  title="View Load Invoice"
                                  placement="top"
                                >
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setLoadingInvoiceId(
                                        transaction.transaction_id
                                      );
                                      try {
                                        const invoice = await getInvoice(
                                          transaction.transaction_id
                                        );
                                        if (invoice?.presigned_url) {
                                          window.open(
                                            invoice.presigned_url,
                                            '_blank'
                                          );
                                        } else {
                                          toast.error(
                                            'Could not get invoice URL'
                                          );
                                        }
                                      } catch (err) {
                                        toast.error('Failed to view invoice');
                                      } finally {
                                        setLoadingInvoiceId(null);
                                      }
                                    }}
                                    className="flex items-center justify-center rounded-[4px] border border-bordergray p-2 disabled:opacity-50"
                                    disabled={
                                      loadingInvoiceId ===
                                      transaction.transaction_id
                                    }
                                  >
                                    {loadingInvoiceId ===
                                    transaction.transaction_id ? (
                                      <ClipLoader
                                        size={18}
                                        className="h-[17px] w-[17px]"
                                      />
                                    ) : (
                                      <Invoice size={20} />
                                    )}
                                  </button>
                                </Tooltip>
                              ) : (
                                <button
                                  id={`popup-button-${transaction.transaction_id}`}
                                  onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                  ) => {
                                    e.stopPropagation();
                                    const rect =
                                      e.currentTarget.getBoundingClientRect();
                                    setButtonPositions((prev) => ({
                                      ...prev,
                                      [transaction.transaction_id]: {
                                        top: rect.top,
                                        left: rect.left,
                                        width: rect.width,
                                        height: rect.height,
                                      },
                                    }));
                                    togglePopup(transaction.transaction_id);
                                  }}
                                  className="relative"
                                >
                                  <DotsThreeVertical size={24} weight="bold" />
                                  {activePopup === transaction.transaction_id &&
                                    createPortal(
                                      <div
                                        ref={(el) => {
                                          popupRefs.current[
                                            transaction.transaction_id ?? ''
                                          ] = el;
                                        }}
                                        className={`fixed z-40 w-[200px] rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl ${isPreActive && 'opacity-80'}`}
                                        style={{
                                          position: 'fixed',
                                          top: `calc(${buttonPositions[transaction.transaction_id]?.top || 0}px + ${buttonPositions[transaction.transaction_id]?.height || 0}px + 4px)`,
                                          left: `${buttonPositions[transaction.transaction_id]?.left || 0}px`,
                                          transform: 'translate(-80%, 0)',
                                        }}
                                      >
                                        <div>
                                          <button
                                            disabled={isPreActive}
                                            onClick={(e: any) => {
                                              e.stopPropagation();
                                              handleAttachInvoice(transaction);
                                              togglePopup(
                                                transaction.transaction_id
                                              );
                                            }}
                                            className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100 disabled:cursor-not-allowed"
                                          >
                                            <Invoice size={20} />
                                            <p>Attach Invoice</p>
                                          </button>
                                        </div>
                                      </div>,
                                      document.body
                                    )}
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                    </tr>
                    {/* Expanded history */}
                    {isOpen && (
                      <tr className="border-b border-gray-200 bg-secondarybg">
                        <td className="relative bg-secondarybg p-0">
                          <img
                            alt="pie-chart-svg"
                            src="/pie-chart.svg"
                            className="absolute left-2 top-4 h-[25px] w-[25px]"
                          />
                        </td>

                        {/* caret spacer */}
                        <td
                          colSpan={HEADERS.length + 1}
                          className="bg-secondarybg p-0"
                        >
                          <div className="w-full overflow-x-auto">
                            <table className="min-w-full table-auto">
                              <thead>
                                <tr className="bg-transparent">
                                  <td className="relative w-6 px-0 py-0">
                                    <span
                                      className={`absolute left-1.5 top-0 h-full w-px bg-gray-300`}
                                    />
                                  </td>
                                  {PAYMENTS_HEADERS.map((h) => (
                                    <th
                                      key={h}
                                      className="px-4 py-2 text-left text-[12px] font-[500] text-graytertiary"
                                    >
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {transaction?.payments.map((s, i) => {
                                  const created = s.created_at
                                    ? new Date(s.created_at)
                                    : undefined;
                                  const isLast =
                                    i ===
                                    (transaction?.payments?.length ?? 1) - 1;
                                  const displayAmount = toDisplayAmount(
                                    s?.amount
                                  );

                                  return (
                                    <tr key={`${s.payment_id ?? i}`}>
                                      <td className="relative w-6 px-0 py-0">
                                        <span
                                          className={`absolute left-1.5 top-0 w-px bg-gray-300 ${
                                            isLast ? 'h-1/2' : 'h-full'
                                          }`}
                                        />
                                        <span className="absolute left-1.5 top-1/2 w-4 -translate-y-1/2 border-t border-gray-300" />
                                      </td>

                                      <td className="px-4 py-1 text-sm text-primary">
                                        {created
                                          ? formatDate(created, false)
                                          : '--'}
                                      </td>
                                      <td
                                        className={`px-4 py-1 text-sm text-primary`}
                                      >
                                        {s.payment_context_display_name}
                                      </td>
                                      <td
                                        className={`flex h-[42px] max-h-[42px] flex-row items-center gap-x-2 overflow-hidden px-4 py-1 text-[14px] text-primary`}
                                      >
                                        {getStatusBreakdownIcon(
                                          s?.status_display_name || ''
                                        )}{' '}
                                        {getStatusBreakdownText(
                                          s?.status_display_name || ''
                                        )}
                                      </td>
                                      <td
                                        className={`px-4 py-1 text-sm ${getDisplayAmountClass(displayAmount)}`}
                                      >
                                        {formatCurrency(displayAmount)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ClipLoader
              color="#1D213B"
              loading={isLoading}
              size={60}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </div>
        )}
      </div>
    );
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(transactionsCount / perPage));

  // A count failure means totals/last-page are unknown, so this banner sits
  // ABOVE the pagination row (which still provides prev/next so a user isn't
  // stranded mid-list) to explain why the count is missing and offer a retry.
  const renderCountErrorBanner = () => (
    <div className="flex w-full shrink-0 flex-row flex-wrap items-center justify-between gap-2 border-x-[1px] border-t-[1px] border-bordergray bg-white px-4 py-3">
      <p className="flex items-center gap-x-2 text-[14px] text-graysecondary">
        <WarningIcon size={20} className="text-orange-500" />
        Couldn&apos;t load the transaction count — the total may be inaccurate.
      </p>
      {onRetryCount && <RetryButton onClick={onRetryCount} />}
    </div>
  );

  const renderPagination = () => (
    <>
      {/* Mobile pagination */}
      <div className="flex w-full shrink-0 items-center justify-between rounded-b-[4px] border-[1px] border-bordergray bg-white px-3 py-2 md:hidden">
        <button
          onClick={() => handlePageChange(currentPage - 1, perPage)}
          disabled={currentPage <= 1}
          className="rounded p-1 hover:bg-gray-100 disabled:opacity-30"
        >
          <CaretLeft size={20} className="text-primary" />
        </button>
        <span className="text-sm text-primary">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1, perPage)}
          disabled={currentPage >= totalPages}
          className="rounded p-1 hover:bg-gray-100 disabled:opacity-30"
        >
          <CaretRight size={20} className="text-primary" />
        </button>
      </div>

      {/* Desktop/tablet pagination */}
      <div className="hidden w-full shrink-0 flex-row flex-wrap items-center justify-between gap-2 rounded-b-[4px] border-[1px] border-bordergray bg-white px-4 py-3 md:flex">
        {!isLoadingCount ? (
          <div className="flex flex-row flex-wrap items-center gap-2 md:w-[50%]">
            {transactionsCount > 0 && (
              <p>
                <span className="text-graytertiary">Showing</span> {offset + 1}{' '}
                - {Math.min(offset + limit, transactionsCount)}{' '}
                <span className="text-graytertiary">of</span>{' '}
                {transactionsCount}{' '}
                <span className="text-graytertiary">transactions</span>
              </p>
            )}
            <Select
              value={perPage}
              onChange={handlePerPageChange}
              placeholder="Select Truck"
              className={`min-w-[100px]`}
            >
              <Select.Option value={20}>20 per page</Select.Option>
              <Select.Option value={50}>50 per page</Select.Option>
              <Select.Option value={100}>100 per page</Select.Option>
            </Select>
          </div>
        ) : (
          <ClipLoader
            color="#1D213B"
            loading={isLoadingCount}
            size={20}
            aria-label="Loading Spinner"
            data-testid="loader"
            className="flex h-full w-full items-center justify-center"
          />
        )}
        <Pagination
          current={currentPage}
          pageSize={perPage}
          total={transactionsCount}
          onChange={handlePageChange}
          showSizeChanger={false}
        />
      </div>
    </>
  );

  return (
    <DemoDataWrapper showBadge={isPreActive}>
      <div className="flex h-[calc(100vh-200px)] w-full flex-col overflow-hidden rounded-[4px] border-l-[1px] border-r-[1px] border-t-[1px] border-bordergray bg-white">
        {transactions && transactions?.length > 0 ? (
          <>
            <div className="min-h-0 w-full flex-1 overflow-auto">
              {renderTable()}
            </div>
            {/* List loaded. On a count failure, show the banner ALONGSIDE
                pagination — the nav (prev/next) still lets a user off page 2. */}
            {isCountError && renderCountErrorBanner()}
            {renderPagination()}
          </>
        ) : isLoading ? (
          <>
            <div className="flex min-h-0 w-full flex-1 items-center justify-center">
              <ClipLoader
                color="#1D213B"
                loading={isLoading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
              />
            </div>
            {isCountError && renderCountErrorBanner()}
            {renderPagination()}
          </>
        ) : isError ? (
          // The LIST query failed — failed ≠ empty, so never render the normal
          // "No Transactions Found" state. (A count-only failure does not reach
          // here; it shows the inline banner above.)
          <TableStatusPanel
            icon={<WarningIcon size={40} className="text-graysecondary" />}
            message="Failed to load transactions. Please try again."
            action={onRetry && <RetryButton onClick={onRetry} />}
          />
        ) : (
          // List loaded but empty. Surface a count failure here too, so an
          // empty list + failed count isn't mistaken for a clean empty state.
          <>
            <TableStatusPanel
              icon={<Invoice size={40} className="text-graysecondary" />}
              message="No Transactions Found"
            />
            {isCountError && renderCountErrorBanner()}
          </>
        )}
      </div>
    </DemoDataWrapper>
  );
}

export default TransactionTable;
